import {
  IDataModelAddFieldInput,
  IDataModelExtendFactFieldInput,
  IDataModelUpdateFieldInput,
  IDimensionDataModel,
  ISchemaFieldMetaFieldsFragment,
  ITableFieldFieldsFragment,
  ITableJoinMetaFieldsFragment,
  ITableMetaFieldsFragment,
  ITemporalJoinConditionMeta,
} from '../../../../utils/apollo/graphqlSchema';
import {
  IDataModelerDraftEdits,
  IJoinDraftData,
} from '../../../../redux/slices/dataModeler';
import { IReactFlowNodeData, SchemaDataModel } from './types';
import { getViewTables, listToHashMapFromKey } from '../utils';
import { Edge as ReactFlowEdge, Node as ReactFlowNode } from '@reactflow/core';
import { typeParamsInputToTypeParamsFieldsFragment } from '../../../../redux/slices/dataModeler/utils';

const FACT_NODE_ID = 'fact-1';
const FACT_LEFT_HANDLE_ID = 'fact-left-handle';
const FACT_RIGHT_HANDLE_ID = 'fact-right-handle';

export const VIEW_NODE_WIDTH = 480;
export const ROW_HEIGHT = 32;

const VIEW_NODE_X_GAP = 60;
const FIELD_ROW_HEIGHT = 2 * ROW_HEIGHT; // every View field is actually 2 rows
const VIEW_Y_OVERHEAD = 100;
const FACT_Y_POS = 20;
const FACT_X_POS = VIEW_NODE_WIDTH + VIEW_NODE_X_GAP;
const LEFT_DIM_X_POS = 0;
const RIGHT_DIM_X_POS = FACT_X_POS * 2;

const calcHeight = (
  numRows: number,
  numTables: number,
  isTemporalJoin: boolean,
) => {
  const tablesInHeaderOverhead = numTables * ROW_HEIGHT;
  const temporalRowOverhead = isTemporalJoin ? ROW_HEIGHT : 0;
  return (
    FIELD_ROW_HEIGHT * numRows +
    VIEW_Y_OVERHEAD +
    tablesInHeaderOverhead +
    temporalRowOverhead
  );
};

export const calculateInitialNodes = (
  dataModel: SchemaDataModel,
): [ReactFlowNode[], ReactFlowEdge[]] => {
  // XXX need to calculate some tricky stiff here
  let rightHeightOffset = 0;
  let leftHeightOffset = 0;

  const retEdges: ReactFlowEdge[] = [];
  const retNodes: ReactFlowNode<IReactFlowNodeData>[] = [
    {
      id: FACT_NODE_ID,
      type: 'factNode',
      position: { x: FACT_X_POS, y: FACT_Y_POS },
      data: {
        dataModel,
        viewKey: dataModel.fact.key,
        fields: dataModel.fact.fields,
        derivedFields: dataModel.derivedFields,
        leftHandleId: FACT_LEFT_HANDLE_ID,
        rightHandleId: FACT_RIGHT_HANDLE_ID,
        viewTables: getViewTables(dataModel.fact.tableKeys, dataModel.tables),
      },
    },
    ...dataModel.dimensions.map((dim, ix) => {
      // XXX starting from right
      const isLeft: boolean = ix % 2 === 1;
      const x: number = isLeft ? LEFT_DIM_X_POS : RIGHT_DIM_X_POS;
      const y = isLeft ? leftHeightOffset : rightHeightOffset;
      const dimId = `dim-${ix + 1}`;
      const edgeId = `ef1-${dimId}`;
      const sourceHandleId = isLeft
        ? FACT_LEFT_HANDLE_ID
        : FACT_RIGHT_HANDLE_ID;
      retEdges.push({
        id: edgeId,
        source: FACT_NODE_ID,
        target: dimId,
        sourceHandle: sourceHandleId,
      });
      const viewTables = getViewTables(dim.tableKeys, dataModel.tables);
      const isTemporalJoin =
        'temporalCondition' in dim.join ? !!dim.join.temporalCondition : false;
      const dimViewHeight = calcHeight(
        dim.fields.length,
        viewTables.length,
        isTemporalJoin,
      );
      if (isLeft) {
        leftHeightOffset += dimViewHeight;
      } else {
        rightHeightOffset += dimViewHeight;
      }
      return {
        id: dimId,
        type: 'dimensionNode',
        position: { x, y },
        data: {
          dataModel,
          dimension: dim,
          viewKey: dim.key,
          fields: dim.fields,
          join: dim.join,
          isLeft,
          edgeId,
          viewTables,
        },
      };
    }),
  ];
  return [retNodes, retEdges];
};

const addInputToSchemaFieldMeta = (
  addInput: IDataModelAddFieldInput,
): ISchemaFieldMetaFieldsFragment => {
  return {
    deprecated: false,
    description: addInput.description,
    fieldClass: addInput.typeInput.fieldClass,
    filter: addInput.filter,
    groupby: addInput.groupby,
    name: addInput.name ?? addInput.viewColumn,
    tableFields: [
      {
        __typename: 'TableField',
        tableKey: addInput.tableKey,
        column: addInput.column,
        type: addInput.typeInput.type,
        typeParams: typeParamsInputToTypeParamsFieldsFragment(
          addInput.column,
          addInput.typeInput.typeParams,
        ),
      },
    ],
    type: addInput.typeInput.type,
    __typename: 'SchemaFieldMeta',
    key: addInput.key,
    column: addInput.viewColumn,
    derivedDefinition: null,
  };
};

const applyExtendInputs = (
  factFields: ISchemaFieldMetaFieldsFragment[],
  extendIputs: IDataModelExtendFactFieldInput[],
): ISchemaFieldMetaFieldsFragment[] => {
  return factFields.map((factField) => {
    const newTableFields = extendIputs.flatMap(
      (extendInput): ITableFieldFieldsFragment[] => {
        if (extendInput.fieldKey !== factField.key) {
          return [];
        }
        return [
          {
            __typename: 'TableField',
            tableKey: extendInput.tableKey,
            column: extendInput.column,
            type: extendInput.typeInput.type,
            typeParams: typeParamsInputToTypeParamsFieldsFragment(
              extendInput.column,
              extendInput.typeInput.typeParams,
            ),
          },
        ];
      },
    );
    return {
      ...factField,
      tableFields: [...factField.tableFields, ...newTableFields],
    };
  });
};

const joinDataToJoinMeta = (
  joinData: IJoinDraftData,
): ITableJoinMetaFieldsFragment => {
  const temporalCondition: ITemporalJoinConditionMeta | null =
    joinData.temporalCondition
      ? {
          __typename: 'TemporalJoinConditionMeta',
          dimTemporalKeys: [
            ...(joinData.temporalCondition.dimTemporalColumn
              ? [joinData.temporalCondition.dimTemporalColumn]
              : []),
            ...(joinData.temporalCondition.dimSecondTemporalColumn
              ? [joinData.temporalCondition.dimSecondTemporalColumn]
              : []),
          ],
          factTemporalKey: joinData.temporalCondition.factTemporalKey,
          temporalJoinType: joinData.temporalCondition.joinType,
        }
      : null;
  return {
    __typename: 'TableJoinMeta',
    joinPairs: joinData.conditions.map((cond) => {
      return {
        __typename: 'TableJoinPairMeta',
        factFieldKey: cond.factFieldKey,
        dimTableFieldColumn: cond.dimTableColumn,
        relOp: cond.relOp,
      };
    }),
    temporalCondition,
    sqlCondition: joinData.sqlCondition,
  };
};

const applyFieldEdits = (
  field: ISchemaFieldMetaFieldsFragment,
  updateInput: IDataModelUpdateFieldInput | undefined,
): ISchemaFieldMetaFieldsFragment => {
  if (!updateInput) {
    return field;
  }
  return {
    ...field,
    description: updateInput.description ?? field.description,
    name: updateInput.name ?? field.name,
    filter: updateInput.filter ?? field.filter,
    groupby: updateInput.groupby ?? field.groupby,
    deprecated: updateInput.deprecated ?? field.deprecated,
  };
};

export const applySchemaEdits = (
  dataModel: SchemaDataModel,
  draftEdits: IDataModelerDraftEdits | null,
): SchemaDataModel => {
  if (!draftEdits) {
    return dataModel;
  }
  const addFactInputs = draftEdits.draftFieldsData.flatMap((draftField) => {
    if (!draftField.addInput || draftField.viewKey !== dataModel.fact.key) {
      return [];
    }
    return [draftField.addInput];
  });
  const extendFactInputs = draftEdits.draftFieldsData.flatMap((draftField) => {
    if (!draftField.extendInput) {
      return [];
    }
    return [draftField.extendInput];
  });
  const editFieldInputs = listToHashMapFromKey(
    draftEdits.updatedFieldsInputs,
    'key',
  );

  const factFields: ISchemaFieldMetaFieldsFragment[] = [
    ...dataModel.fact.fields.map((field) =>
      applyFieldEdits(field, editFieldInputs[field.key]),
    ),
    ...addFactInputs.map(addInputToSchemaFieldMeta),
  ];

  const editJoinInputs = listToHashMapFromKey(
    draftEdits.editJoinsData,
    'viewKey',
  );

  return {
    ...dataModel,
    dimensions: [
      ...dataModel.dimensions.map((dimension) => {
        return {
          ...dimension,
          fields: dimension.fields.map((field) =>
            applyFieldEdits(field, editFieldInputs[field.key]),
          ),
          join: editJoinInputs[dimension.key]
            ? joinDataToJoinMeta(editJoinInputs[dimension.key]!.joinData)
            : dimension.join,
        };
      }),
      ...draftEdits.addedDimensionsData.map((draftDim): IDimensionDataModel => {
        const addInputs = draftEdits.draftFieldsData.flatMap((draftField) => {
          if (!draftField.addInput || draftField.viewKey !== draftDim.viewKey) {
            return [];
          }
          return [draftField.addInput];
        });
        return {
          customColumnDefinitions: [], // not relevant for entity relations
          fields: addInputs.map(addInputToSchemaFieldMeta),
          join: joinDataToJoinMeta(draftDim.joinData),
          key: draftDim.viewKey,
          tableKeys: [draftDim.tableKey],
          __typename: 'DimensionDataModel',
        };
      }),
    ],
    fact: {
      ...dataModel.fact,
      tableKeys: [
        ...dataModel.fact.tableKeys,
        ...draftEdits.addedFactsData.map((draftFact) => draftFact.tableKey),
      ],
      fields: applyExtendInputs(factFields, extendFactInputs),
    },
    tables: [
      ...dataModel.tables,
      ...draftEdits.addedFactsData.map(
        (draftFact): ITableMetaFieldsFragment => {
          return {
            __typename: 'TableMeta',
            key: draftFact.tableKey,
            name: draftFact.sqlTableName,
            dateRangePredicateColumn: null,
            partitionDelegates: [],
          };
        },
      ),
      ...draftEdits.addedDimensionsData.map(
        (draftDim): ITableMetaFieldsFragment => {
          return {
            __typename: 'TableMeta',
            key: draftDim.tableKey,
            name: draftDim.sqlTableName,
            dateRangePredicateColumn: null,
            partitionDelegates: [],
          };
        },
      ),
    ],
  };
};
